/**
 * ErrorNotification
 *
 * A global error notification system that stacks multiple MUI Snackbar/Alert
 * notifications. Each notification auto-dismisses after 6 seconds.
 *
 * Usage:
 *  1. Wrap your app with <ErrorNotificationProvider>.
 *  2. Use the useErrorNotification() hook anywhere to push notifications.
 *  3. Alternatively, errors emitted via apiErrorEmitter are picked up
 *     automatically (the provider subscribes on mount).
 *
 * This component complements the existing ErrorContext / ErrorSnackbar pair
 * by providing stacked, independently-dismissible notifications.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { Snackbar, Alert, AlertColor, Typography, Box, IconButton, Slide } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import apiErrorEmitter, { ApiErrorEvent, ApiErrorSeverity } from '../../services/apiErrorEmitter';

// ---------- Types ----------

export interface Notification {
  id: string;
  message: string;
  severity: AlertColor; // 'error' | 'warning' | 'info' | 'success'
  details?: string;
  timestamp: number;
}

export interface ErrorNotificationContextType {
  /** Push a new notification. */
  notify: (message: string, severity?: AlertColor, details?: string) => void;
  /** Dismiss a single notification by id. */
  dismiss: (id: string) => void;
  /** Dismiss all notifications. */
  dismissAll: () => void;
  /** Active notification list. */
  notifications: Notification[];
}

// ---------- Helpers ----------

let notifIdCounter = 0;

function createNotifId(): string {
  notifIdCounter += 1;
  return `notif_${Date.now()}_${notifIdCounter}`;
}

/** Map ApiErrorSeverity to MUI AlertColor. */
function mapSeverity(s: ApiErrorSeverity): AlertColor {
  switch (s) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'error';
  }
}

/** Max simultaneous visible notifications. */
const MAX_VISIBLE = 5;

/** Auto-dismiss duration in ms. */
const AUTO_DISMISS_MS = 6000;

// ---------- Context ----------

const ErrorNotificationContext = createContext<ErrorNotificationContextType | undefined>(
  undefined
);

// ---------- Slide transition ----------

function SlideUp(props: TransitionProps & { children: React.ReactElement }) {
  return <Slide {...props} direction="up" />;
}

// ---------- Single notification item ----------

interface NotificationItemProps {
  notification: Notification;
  index: number;
  onDismiss: (id: string) => void;
}

const GOLD = '#8B2438';

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  index,
  onDismiss,
}) => {
  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    onDismiss(notification.id);
  };

  // Stack offset: each subsequent notification is shifted up
  const bottomOffset = 24 + index * 72;

  return (
    <Snackbar
      open
      autoHideDuration={AUTO_DISMISS_MS}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={SlideUp}
      sx={{
        position: 'fixed',
        bottom: `${bottomOffset}px !important`,
        right: '24px !important',
        left: 'auto !important',
        top: 'auto !important',
        maxWidth: 440,
        zIndex: (theme) => theme.zIndex.snackbar + index,
      }}
    >
      <Alert
        severity={notification.severity}
        variant="filled"
        onClose={handleClose}
        action={
          <IconButton size="small" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          width: '100%',
          alignItems: 'center',
          // Luxury styling
          ...(notification.severity === 'error' && { backgroundColor: '#c62828' }),
          ...(notification.severity === 'warning' && { backgroundColor: '#e65100' }),
          ...(notification.severity === 'info' && {
            backgroundColor: '#1a1a1a',
            border: `1px solid ${GOLD}`,
          }),
          ...(notification.severity === 'success' && { backgroundColor: '#16A34A' }),
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.4, fontWeight: 500 }}>
            {notification.message}
          </Typography>
          {notification.details && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, opacity: 0.8, fontStyle: 'italic' }}
            >
              {notification.details}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

// ---------- Provider ----------

export const ErrorNotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const notify = useCallback(
    (message: string, severity: AlertColor = 'error', details?: string) => {
      const newNotif: Notification = {
        id: createNotifId(),
        message,
        severity,
        details,
        timestamp: Date.now(),
      };

      setNotifications((prev) => {
        // Keep only the most recent MAX_VISIBLE notifications
        const updated = [...prev, newNotif];
        if (updated.length > MAX_VISIBLE) {
          return updated.slice(updated.length - MAX_VISIBLE);
        }
        return updated;
      });
    },
    []
  );

  // Subscribe to apiErrorEmitter so Axios interceptor errors are shown automatically
  useEffect(() => {
    const unsubscribe = apiErrorEmitter.subscribe((event: ApiErrorEvent) => {
      notify(event.message, mapSeverity(event.severity), event.details);
    });
    return unsubscribe;
  }, [notify]);

  const value: ErrorNotificationContextType = {
    notify,
    dismiss,
    dismissAll,
    notifications,
  };

  return (
    <ErrorNotificationContext.Provider value={value}>
      {children}
      {/* Render stacked notifications */}
      {notifications.map((n, idx) => (
        <NotificationItem
          key={n.id}
          notification={n}
          index={idx}
          onDismiss={dismiss}
        />
      ))}
    </ErrorNotificationContext.Provider>
  );
};

// ---------- Hook ----------

export const useErrorNotification = (): ErrorNotificationContextType => {
  const context = useContext(ErrorNotificationContext);
  if (!context) {
    throw new Error(
      'useErrorNotification must be used within an <ErrorNotificationProvider>.'
    );
  }
  return context;
};

export default ErrorNotificationProvider;
